/**
 * Touch Gesture Handler Utility
 * Provides comprehensive touch gesture recognition and handling
 */

interface TouchPoint {
  x: number;
  y: number;
  timestamp: number;
}

interface GestureState {
  isActive: boolean;
  startPoint: TouchPoint;
  currentPoint: TouchPoint;
  deltaX: number;
  deltaY: number;
  distance: number;
  velocity: number;
  direction: 'left' | 'right' | 'up' | 'down' | null;
}

interface SwipeOptions {
  threshold: number;
  velocityThreshold: number;
  maxTime: number;
  preventScroll: boolean;
}

interface PinchOptions {
  threshold: number;
  maxTime: number;
}

interface TapOptions {
  maxDistance: number;
  maxTime: number;
  doubleTapTime: number;
}

type GestureCallback = (gesture: GestureState, event: TouchEvent) => void;
type SwipeCallback = (direction: string, distance: number, velocity: number, event: TouchEvent) => void;
type PinchCallback = (scale: number, event: TouchEvent) => void;
type TapCallback = (point: TouchPoint, event: TouchEvent) => void;

export class TouchGestureHandler {
  private element: HTMLElement;
  private gestureState: GestureState;
  private options: {
    swipe: SwipeOptions;
    pinch: PinchOptions;
    tap: TapOptions;
  };
  
  private callbacks: {
    swipe: Set<SwipeCallback>;
    pinch: Set<PinchCallback>;
    tap: Set<TapCallback>;
    doubleTap: Set<TapCallback>;
    gesture: Set<GestureCallback>;
  };

  private lastTapTime: number = 0;
  private tapCount: number = 0;
  private initialPinchDistance: number = 0;
  private isListening: boolean = false;

  constructor(element: HTMLElement, options: Partial<{
    swipe: Partial<SwipeOptions>;
    pinch: Partial<PinchOptions>;
    tap: Partial<TapOptions>;
  }> = {}) {
    this.element = element;
    
    this.options = {
      swipe: {
        threshold: options.swipe?.threshold ?? 50,
        velocityThreshold: options.swipe?.velocityThreshold ?? 0.5,
        maxTime: options.swipe?.maxTime ?? 1000,
        preventScroll: options.swipe?.preventScroll ?? true
      },
      pinch: {
        threshold: options.pinch?.threshold ?? 10,
        maxTime: options.pinch?.maxTime ?? 2000
      },
      tap: {
        maxDistance: options.tap?.maxDistance ?? 10,
        maxTime: options.tap?.maxTime ?? 300,
        doubleTapTime: options.tap?.doubleTapTime ?? 300
      }
    };

    this.gestureState = this.createInitialState();
    this.callbacks = {
      swipe: new Set(),
      pinch: new Set(),
      tap: new Set(),
      doubleTap: new Set(),
      gesture: new Set()
    };

    this.init();
  }

  private createInitialState(): GestureState {
    return {
      isActive: false,
      startPoint: { x: 0, y: 0, timestamp: 0 },
      currentPoint: { x: 0, y: 0, timestamp: 0 },
      deltaX: 0,
      deltaY: 0,
      distance: 0,
      velocity: 0,
      direction: null
    };
  }

  private init(): void {
    this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    this.element.addEventListener('touchcancel', this.handleTouchCancel.bind(this), { passive: true });
    
    this.isListening = true;
  }

  private handleTouchStart(event: TouchEvent): void {
    const touch = event.touches[0];
    if (!touch) return;

    const now = Date.now();
    
    this.gestureState = {
      isActive: true,
      startPoint: { x: touch.clientX, y: touch.clientY, timestamp: now },
      currentPoint: { x: touch.clientX, y: touch.clientY, timestamp: now },
      deltaX: 0,
      deltaY: 0,
      distance: 0,
      velocity: 0,
      direction: null
    };

    // Handle pinch gestures
    if (event.touches.length === 2 && event.touches[0] && event.touches[1]) {
      this.initialPinchDistance = this.calculateDistance(event.touches[0], event.touches[1]);
    }

    this.notifyGestureCallbacks(event);
  }

  private handleTouchMove(event: TouchEvent): void {
    if (!this.gestureState.isActive) return;

    const touch = event.touches[0];
    if (!touch) return;

    const now = Date.now();
    
    this.gestureState.currentPoint = { x: touch.clientX, y: touch.clientY, timestamp: now };
    this.gestureState.deltaX = this.gestureState.currentPoint.x - this.gestureState.startPoint.x;
    this.gestureState.deltaY = this.gestureState.currentPoint.y - this.gestureState.startPoint.y;
    this.gestureState.distance = Math.sqrt(
      this.gestureState.deltaX ** 2 + this.gestureState.deltaY ** 2
    );

    const timeDelta = now - this.gestureState.startPoint.timestamp;
    this.gestureState.velocity = timeDelta > 0 ? this.gestureState.distance / timeDelta : 0;

    // Determine direction
    if (Math.abs(this.gestureState.deltaX) > Math.abs(this.gestureState.deltaY)) {
      this.gestureState.direction = this.gestureState.deltaX > 0 ? 'right' : 'left';
    } else {
      this.gestureState.direction = this.gestureState.deltaY > 0 ? 'down' : 'up';
    }

    // Handle pinch gestures
    if (event.touches.length === 2 && event.touches[0] && event.touches[1]) {
      const currentDistance = this.calculateDistance(event.touches[0], event.touches[1]);
      const scale = currentDistance / this.initialPinchDistance;
      
      if (Math.abs(scale - 1) > this.options.pinch.threshold / 100) {
        this.notifyPinchCallbacks(scale, event);
      }
    }

    // Prevent scrolling if configured
    if (this.options.swipe.preventScroll && 
        this.gestureState.distance > this.options.swipe.threshold / 2) {
      event.preventDefault();
    }

    this.notifyGestureCallbacks(event);
  }

  private handleTouchEnd(event: TouchEvent): void {
    if (!this.gestureState.isActive) return;

    const now = Date.now();
    const timeDelta = now - this.gestureState.startPoint.timestamp;

    // Check for swipe gesture
    if (this.gestureState.distance > this.options.swipe.threshold &&
        this.gestureState.velocity > this.options.swipe.velocityThreshold &&
        timeDelta < this.options.swipe.maxTime &&
        this.gestureState.direction) {
      this.notifySwipeCallbacks(
        this.gestureState.direction,
        this.gestureState.distance,
        this.gestureState.velocity,
        event
      );
    }
    // Check for tap gesture
    else if (this.gestureState.distance < this.options.tap.maxDistance &&
             timeDelta < this.options.tap.maxTime) {
      this.handleTap(event);
    }

    this.gestureState.isActive = false;
    this.notifyGestureCallbacks(event);
  }

  private handleTouchCancel(event: TouchEvent): void {
    this.gestureState.isActive = false;
    this.notifyGestureCallbacks(event);
  }

  private handleTap(event: TouchEvent): void {
    const now = Date.now();
    const timeSinceLastTap = now - this.lastTapTime;

    if (timeSinceLastTap < this.options.tap.doubleTapTime) {
      this.tapCount++;
    } else {
      this.tapCount = 1;
    }

    this.lastTapTime = now;

    // Use setTimeout to wait for potential double tap
    setTimeout(() => {
      if (this.tapCount === 1) {
        this.notifyTapCallbacks(this.gestureState.startPoint, event);
      } else if (this.tapCount === 2) {
        this.notifyDoubleTapCallbacks(this.gestureState.startPoint, event);
      }
      this.tapCount = 0;
    }, this.options.tap.doubleTapTime);
  }

  private calculateDistance(touch1: Touch, touch2: Touch): number {
    const deltaX = touch2.clientX - touch1.clientX;
    const deltaY = touch2.clientY - touch1.clientY;
    return Math.sqrt(deltaX ** 2 + deltaY ** 2);
  }

  private notifySwipeCallbacks(direction: string, distance: number, velocity: number, event: TouchEvent): void {
    this.callbacks.swipe.forEach(callback => {
      try {
        callback(direction, distance, velocity, event);
      } catch (error) {
        console.error('Error in swipe callback:', error);
      }
    });
  }

  private notifyPinchCallbacks(scale: number, event: TouchEvent): void {
    this.callbacks.pinch.forEach(callback => {
      try {
        callback(scale, event);
      } catch (error) {
        console.error('Error in pinch callback:', error);
      }
    });
  }

  private notifyTapCallbacks(point: TouchPoint, event: TouchEvent): void {
    this.callbacks.tap.forEach(callback => {
      try {
        callback(point, event);
      } catch (error) {
        console.error('Error in tap callback:', error);
      }
    });
  }

  private notifyDoubleTapCallbacks(point: TouchPoint, event: TouchEvent): void {
    this.callbacks.doubleTap.forEach(callback => {
      try {
        callback(point, event);
      } catch (error) {
        console.error('Error in double tap callback:', error);
      }
    });
  }

  private notifyGestureCallbacks(event: TouchEvent): void {
    this.callbacks.gesture.forEach(callback => {
      try {
        callback(this.gestureState, event);
      } catch (error) {
        console.error('Error in gesture callback:', error);
      }
    });
  }

  // Public API
  public onSwipe(callback: SwipeCallback): () => void {
    this.callbacks.swipe.add(callback);
    return () => this.callbacks.swipe.delete(callback);
  }

  public onPinch(callback: PinchCallback): () => void {
    this.callbacks.pinch.add(callback);
    return () => this.callbacks.pinch.delete(callback);
  }

  public onTap(callback: TapCallback): () => void {
    this.callbacks.tap.add(callback);
    return () => this.callbacks.tap.delete(callback);
  }

  public onDoubleTap(callback: TapCallback): () => void {
    this.callbacks.doubleTap.add(callback);
    return () => this.callbacks.doubleTap.delete(callback);
  }

  public onGesture(callback: GestureCallback): () => void {
    this.callbacks.gesture.add(callback);
    return () => this.callbacks.gesture.delete(callback);
  }

  public getState(): GestureState {
    return { ...this.gestureState };
  }

  public isActive(): boolean {
    return this.gestureState.isActive;
  }

  public destroy(): void {
    if (!this.isListening) return;

    this.element.removeEventListener('touchstart', this.handleTouchStart.bind(this));
    this.element.removeEventListener('touchmove', this.handleTouchMove.bind(this));
    this.element.removeEventListener('touchend', this.handleTouchEnd.bind(this));
    this.element.removeEventListener('touchcancel', this.handleTouchCancel.bind(this));

    this.callbacks.swipe.clear();
    this.callbacks.pinch.clear();
    this.callbacks.tap.clear();
    this.callbacks.doubleTap.clear();
    this.callbacks.gesture.clear();

    this.isListening = false;
  }
}